import React, { useState } from "react";
import { explorer, getStory } from "../../data/impactStories";
import { HardHat, Droplets, Truck, Users, Recycle, X, ArrowUpRight } from "lucide-react";

const icons = { HardHat, Droplets, Truck, Users, Recycle };

export default function ProjectExplorer({ onOpenStory }) {
  const [active, setActive] = useState(null);
  const spot = explorer.hotspots.find((h) => h.id === active);

  return (
    <section id="explorer" data-testid="ri-project-explorer" className="py-24 lg:py-32 bg-[#08080a] scroll-mt-20">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-12 reveal">
          <div className="text-[11px] uppercase tracking-[0.3em] text-amber-accent mb-4">Project Explorer</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.02]">
            One site. <span className="text-amber-accent">Every decision.</span>
          </h2>
          <p className="mt-5 text-white/65 leading-relaxed max-w-2xl">
            {explorer.project}. Tap the markers to see how responsibility is engineered
            into every corner of a live scheme.
          </p>
        </div>

        <div className="reveal relative rounded-2xl overflow-hidden border border-white/10">
          <img src={explorer.image} alt={explorer.project} className="w-full h-[420px] sm:h-[540px] lg:h-[640px] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30 pointer-events-none" />

          {explorer.hotspots.map((h) => {
            const Icon = icons[h.icon];
            return (
              <button
                key={h.id}
                data-testid={`hotspot-${h.id}`}
                onClick={() => setActive(active === h.id ? null : h.id)}
                style={{ left: `${h.x}%`, top: `${h.y}%` }}
                className={`absolute -translate-x-1/2 -translate-y-1/2 group ${active === h.id ? "z-20" : "z-10"}`}
                aria-label={h.title}
              >
                <span className={`hotspot-ring absolute inset-0 rounded-full ${active === h.id ? "hidden" : ""}`} />
                <span className={`relative flex items-center justify-center w-11 h-11 rounded-full border-2 backdrop-blur transition-all duration-300 ${
                  active === h.id ? "bg-[#F5D000] border-[#F5D000] scale-110" : "bg-black/55 border-[#F5D000]/70 hover:bg-black/80"
                }`}>
                  <Icon className={`w-4.5 h-4.5 w-5 h-5 ${active === h.id ? "text-black" : "text-[#F5D000]"}`} />
                </span>
                <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-0.5 rounded bg-black/70 text-[10px] text-white/85 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  {h.title}
                </span>
              </button>
            );
          })}

          {/* Panel */}
          {spot && (
            <div data-testid="hotspot-panel" className="absolute bottom-4 left-4 right-4 sm:left-auto sm:w-[400px] ri-fade-up rounded-2xl bg-black/85 backdrop-blur-xl border border-[#F5D000]/30 p-6 z-30">
              <button
                data-testid="hotspot-panel-close"
                onClick={() => setActive(null)}
                className="absolute top-4 right-4 w-7 h-7 rounded-full border border-white/15 flex items-center justify-center text-white/60 hover:text-white"
              >
                <X className="w-3.5 h-3.5" />
              </button>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#F5D000] mb-1.5">{spot.category}</div>
              <h3 className="font-display text-xl font-bold text-white mb-2">{spot.title}</h3>
              <p className="text-sm text-white/70 leading-relaxed mb-4">{spot.text}</p>
              {spot.storyId && (
                <button
                  data-testid="hotspot-open-story"
                  onClick={() => onOpenStory(getStory(spot.storyId))}
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#F5D000] hover:gap-3 transition-all"
                >
                  Related Impact Story <ArrowUpRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
